import { useState, useEffect, useRef, useCallback } from "react"
import PubSub from "pubsub-js"
import { navigate } from "aldoo-ra/Navigate"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { showMessageDialog } from "aldoo-ra/MessageDialog"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import Value from "aldoo-ra/Value"
const { SetBreakAwaitAuth, useSigninWithProvider, useLogout } = PlatformAuth

//dialog type enum
const DialogType = {
  MAGIC_LINK: "magicLink",
  PROVIDER: "provider",
  SUCCESS: "success",
  SIGNIN: "signin",
}

let instance = null

const useSigninController = () => {
  // State for dialog management
  const [provider, setProvider] = useState({})
  // DialogType
  const [dialog, setDialog] = useState("")
  //localization for this component
  const texts = TextBundle({ id: "signin" })

  // Authentication hooks
  const {
    state: signinState,
    error,
    signin: signinWithProvider,
  } = useSigninWithProvider()

  const [auth, setAuth] = PlatformAuth.useAuth()

  const [userProfile] = Value("user-profile")
  const redirectTarget = useRef(null)
  const requireField = useRef(null)
  const errorHandled = useRef(false)

  const errorToMessage = (error) => {
    // Convert error codes to user-friendly messages
    const errorMessages = {
      email_required: "Email address is required",
      invalid_email: "Please enter a valid email address",
      invalid_user: "Invalid credentials",
      access_denied: "You don't have access to this system. Please contact your administrator.",
      // Add more error mappings as needed
    }

    // Return user-friendly message or fallback to original error
    return errorMessages[error] || error
  }

  //respond to errors
  useEffect(() => {
    if (!error) {
      errorHandled.current = false
      return
    }

    // Prevent handling the same error multiple times
    if (errorHandled.current) return
    errorHandled.current = true

    // Close the signin dialog immediately when error occurs
    setDialog("")
    // Reset provider state to prevent any re-triggering
    setProvider({})

    showMessageDialog({
      title: texts?.error_dialog_title,
      message: errorToMessage(error),
      btnLabel: "OK",
      showCloseX: false,
      onConfirm: () => {
        // Dialog is already closed, this just dismisses the error message
        errorHandled.current = false
      }
    })
  }, [error, texts?.error_dialog_title])

  // Clear error when opening signin dialog
  useEffect(() => {
    if (dialog === "signin") {
      // Clear any previous errors when showing signin dialog
      // This ensures fresh state for each signin attempt
    }
  }, [dialog])

  //Interface
  const isDialogOpen = (dialogType) => dialog === dialogType
  const performLogout = useLogout()
  //return to the sign-in dialog
  const backToSignIn = () => {
    SetBreakAwaitAuth(true)
    setDialog("signin")
  }
  const closeDialog = () => setDialog("")
  const loginConfirm = () => {
    //set the new url on login confirm
    if (redirectTarget.current) {
      const current = window.location.pathname + window.location.search
      const target = new URL(redirectTarget.current, window.location.origin)
      const targetFull = target.pathname + target.search

      if (current !== targetFull) {
        window.location.href = redirectTarget.current
      }
    }
    setDialog("")
  }

  const logout = () => {
    //confirm before logout
    showPromptDialog({
      title: instance.data?.logout_dialog_title,
      message: instance.data?.logout_dialog_message,
      onConfirm: async () => {
        await performLogout()
        navigate("/")
      },
    })
  }


  // Start signing in with a provider
  const signin = async (selectedProvider, data) => {
    //set any parameters
    selectedProvider.data = data

    // Add require field if present (for admin access control)
    if (requireField.current) {
      selectedProvider.require = requireField.current
    }

    //set the active provider
    setProvider(selectedProvider)

    // For email provider, validate email first before changing dialog
    if (selectedProvider.id === "email") {
      const email = selectedProvider.data // data is the email string directly


      // Check if email is provided
      if (!email || typeof email !== 'string' || !email.trim()) {
        return showMessageDialog({
          title: instance?.data?.error_dialog_title,
          message: errorToMessage("email_required"),
          btnLabel: "OK",
          showCloseX: false,
          onConfirm: () => {
            setDialog("") // Close signin dialog
          }
        })
      }

      // Validate email format
      const emailValidator = await import("email-validator")
      if (!emailValidator.default.validate(email.trim())) {
        return showMessageDialog({
          title: instance?.data?.error_dialog_title,
          message: errorToMessage("invalid_email"),
          btnLabel: "OK",
          showCloseX: false,
          onConfirm: () => {
            setDialog("") // Close signin dialog
          }
        })
      }

    }

    // Trigger sign-in first to check access before showing magic link dialog
    // The dialog will be set by the signin state changes
    await signinWithProvider(selectedProvider)
  }

  useEffect(() => {
    // Subscribe to events, invoke the signin dialog from anywhere
    // once the login is done, you'll be redirected to the target redirect
    const signinToken = PubSub.subscribe("signin", async (msg, data) => {
      const { redirect, forceDialog, require, email, provider } = data || {}
      redirectTarget.current = redirect
      requireField.current = require // Store require field for admin access

      // If email and provider are provided, auto-start the signin flow
      if (email && provider) {
        // Set the provider data with the email
        const emailProvider = { id: provider, data: email }
        await signin(emailProvider, email)
        return
      }

      // Force dialog shows signin regardless of auth state (for SessionMonitor)
      if (forceDialog) {
        setDialog("signin")
        return
      }

      // If require field is set (e.g., "aldoo" for admin), validate permission
      if (auth && require) {
        // Check if user has the required permission
        try {
          const { PermissionAPI } = await import("aldoo-ra/Permissions")
          const hasPermission = await PermissionAPI.hasPermission(auth._id, `role:${require}`)

          if (hasPermission) {
            return navigate(redirect || "/")
          } else {
            // Clear invalid auth
            setAuth(null)
            setDialog("signin")
            return
          }
        } catch (error) {
          // On error, clear auth and show signin dialog
          setAuth(null)
          setDialog("signin")
          return
        }
      }

      if (auth) {
        return navigate(redirect || "/")
      }
      // If not signed in, show the sign-in dialog
      setDialog("signin")
    })

    // Unsubscribe from events
    return () => PubSub.unsubscribe(signinToken)
  }, [auth, signin])

  // Track sign-in state
  useEffect(() => {
    if (signinState === "success") {
      setDialog(DialogType.SUCCESS)
    } else if (signinState === "waiting-auth" && !error) {
      // Only show magic link dialog if we're waiting for auth and there's no error
      setDialog(DialogType.MAGIC_LINK)
    }
  }, [signinState, error])

  instance = {
    data: texts,
    dialogType: dialog,
    provider,
    userProfile,
    logout,
    signin,
    closeDialog,
    loginConfirm,
    backToSignIn,
    DialogType,
    isDialogOpen,
  }

  return instance
}

export default useSigninController
